// Trade calculation utilities for dashboard insights

import { convertToDisplayCurrency } from '../hooks/useDashboardCurrency'

interface Trade {
  id: string
  symbol: string
  long_short: 'long' | 'short'
  entry_price: number
  stop_loss: number
  take_profit: number
  current_price?: number | null
  unrealized_pnl?: number | null
  unrealized_pnl_percentage?: number | null
  quantity?: number | null
  contract_size?: number | null
  currency?: string | null
  exit_date?: string | null
  total_entry_amount?: number | null
  position_size?: number | null
  account_id?: string | null
  pnl?: number | null
  market?: string | null
  exchange?: string | null
  tags?: string[] | null
}

export const HIGH_RISK_EXPOSURE_THRESHOLD = 5

export interface AccountForRiskCalc {
  id: string
  currency: string
  initial_balance?: number | null
}

function convertAmountWithRates(
  amount: number,
  from: string,
  selectedCurrency: string,
  latestRates: Map<string, number>,
): number {
  if (from === selectedCurrency) return amount
  const direct = latestRates.get(`${from}:${selectedCurrency}`)
  if (direct != null) return amount * direct
  const inverse = latestRates.get(`${selectedCurrency}:${from}`)
  if (inverse != null && inverse > 0) return amount / inverse
  return amount
}

/**
 * Calculate portfolio-level P/L % for open positions (weighted by position size, fallback to simple average).
 */
export function calcOpenPositionsTotalPnLPercent(trades: Trade[]): number | null {
  const open = trades.filter(t => !t.exit_date)
  if (open.length === 0) return null

  const withPct = open.filter(
    t =>
      t.unrealized_pnl_percentage !== null &&
      t.unrealized_pnl_percentage !== undefined
  )
  if (withPct.length === 0) return null

  const totalSize = withPct.reduce((sum, t) => {
    const size = t.total_entry_amount ?? t.position_size ?? 0
    return sum + (typeof size === 'number' ? size : 0)
  }, 0)

  if (totalSize > 0) {
    const weighted = withPct.reduce((sum, t) => {
      const size = t.total_entry_amount ?? t.position_size ?? 0
      const w = typeof size === 'number' ? size : 0
      return sum + (t.unrealized_pnl_percentage ?? 0) * w
    }, 0)
    return weighted / totalSize
  }

  const avg =
    withPct.reduce((s, t) => s + (t.unrealized_pnl_percentage ?? 0), 0) /
    withPct.length
  return avg
}

/**
 * Calculate distance percentage between current price and a target level
 */
export function calcDistancePercent(currentPrice: number, targetLevel: number): number {
  return Math.abs((currentPrice - targetLevel) / targetLevel) * 100
}

/**
 * Check if a LONG position is near stop loss (within threshold)
 */
export function isNearStopLossLong(
  currentPrice: number,
  stopLoss: number,
  thresholdPercent: number = 3
): boolean {
  // For LONG: near SL if current price is close to or below SL
  return currentPrice <= stopLoss * (1 + thresholdPercent / 100)
}

/**
 * Check if a SHORT position is near stop loss (within threshold)
 */
export function isNearStopLossShort(
  currentPrice: number,
  stopLoss: number,
  thresholdPercent: number = 3
): boolean {
  // For SHORT: near SL if current price is close to or above SL
  return currentPrice >= stopLoss * (1 - thresholdPercent / 100)
}

/**
 * Check if a LONG position is near take profit (within threshold)
 */
export function isNearTakeProfitLong(
  currentPrice: number,
  takeProfit: number,
  thresholdPercent: number = 3
): boolean {
  // For LONG: near TP if current price is close to or above TP
  return currentPrice >= takeProfit * (1 - thresholdPercent / 100)
}

/**
 * Check if a SHORT position is near take profit (within threshold)
 */
export function isNearTakeProfitShort(
  currentPrice: number,
  takeProfit: number,
  thresholdPercent: number = 3
): boolean {
  // For SHORT: near TP if current price is close to or below TP
  return currentPrice <= takeProfit * (1 + thresholdPercent / 100)
}

/**
 * Check if trade is near stop loss (direction-aware)
 */
export function isNearStopLoss(trade: Trade, thresholdPercent: number = 3): boolean {
  if (!trade.current_price || !trade.stop_loss) return false
  
  if (trade.long_short === 'long') {
    return isNearStopLossLong(trade.current_price, trade.stop_loss, thresholdPercent)
  } else {
    return isNearStopLossShort(trade.current_price, trade.stop_loss, thresholdPercent)
  }
}

/**
 * Check if trade is near take profit (direction-aware)
 */
export function isNearTakeProfit(trade: Trade, thresholdPercent: number = 3): boolean {
  if (!trade.current_price || !trade.take_profit) return false
  
  if (trade.long_short === 'long') {
    return isNearTakeProfitLong(trade.current_price, trade.take_profit, thresholdPercent)
  } else {
    return isNearTakeProfitShort(trade.current_price, trade.take_profit, thresholdPercent)
  }
}

/**
 * Calculate risk percentage for a position (entry to stop loss).
 * When longShort is provided, returns 0 if the stop is on the profit side
 * (long with SL above entry, or short with SL below entry) — no capital at risk.
 */
export function calcRiskPercent(
  entryPrice: number,
  stopLoss: number,
  longShort?: string | null,
): number {
  if (longShort === 'long' && stopLoss >= entryPrice) return 0
  if (longShort === 'short' && stopLoss <= entryPrice) return 0
  return Math.abs((entryPrice - stopLoss) / entryPrice) * 100
}

/**
 * Calculate position risk amount (in currency).
 * When longShort is provided, returns 0 if the stop is on the profit side
 * (long with SL above entry, or short with SL below entry) — no capital at risk.
 */
export function calcPositionRisk(
  entryPrice: number,
  stopLoss: number,
  quantity?: number | null,
  contractSize?: number | null,
  longShort?: string | null,
): number | null {
  const qty = quantity ?? 0
  const contract = contractSize ?? 1

  if (qty === 0) return null
  if (longShort === 'long' && stopLoss >= entryPrice) return 0
  if (longShort === 'short' && stopLoss <= entryPrice) return 0

  const riskPerUnit = Math.abs(entryPrice - stopLoss)
  const units = qty * contract

  return riskPerUnit * units
}

/**
 * Calculate position risk as % of total entry amount (capital at risk).
 */
export function calcPositionRiskPercent(
  entryPrice: number,
  stopLoss: number,
  quantity?: number | null,
  contractSize?: number | null,
  longShort?: string | null,
  totalEntryAmount?: number | null,
): number | null {
  const risk = calcPositionRisk(entryPrice, stopLoss, quantity, contractSize, longShort)
  if (risk === null) return null
  if (totalEntryAmount == null || totalEntryAmount <= 0) return null
  return (risk / totalEntryAmount) * 100
}

/**
 * Get top winners (by unrealized P&L percentage)
 */
export function getTopWinners(trades: Trade[], limit: number = 5): Trade[] {
  return trades
    .filter(t => 
      !t.exit_date && 
      t.current_price !== null && 
      t.current_price !== undefined &&
      t.unrealized_pnl_percentage !== null &&
      t.unrealized_pnl_percentage !== undefined &&
      t.unrealized_pnl_percentage > 0
    )
    .sort((a, b) => (b.unrealized_pnl_percentage ?? 0) - (a.unrealized_pnl_percentage ?? 0))
    .slice(0, limit)
}

/**
 * Get top losers (by unrealized P&L percentage)
 */
export function getTopLosers(trades: Trade[], limit: number = 5): Trade[] {
  return trades
    .filter(t => 
      !t.exit_date && 
      t.current_price !== null && 
      t.current_price !== undefined &&
      t.unrealized_pnl_percentage !== null &&
      t.unrealized_pnl_percentage !== undefined &&
      t.unrealized_pnl_percentage < 0
    )
    .sort((a, b) => (a.unrealized_pnl_percentage ?? 0) - (b.unrealized_pnl_percentage ?? 0))
    .slice(0, limit)
}

/**
 * Get positions near stop loss
 */
export function getPositionsNearStopLoss(
  trades: Trade[], 
  thresholdPercent: number = 3
): Array<Trade & { distance: number }> {
  return trades
    .filter(t => 
      !t.exit_date && 
      t.current_price !== null && 
      t.current_price !== undefined &&
      isNearStopLoss(t, thresholdPercent)
    )
    .map(t => ({
      ...t,
      distance: calcDistancePercent(t.current_price!, t.stop_loss)
    }))
    .sort((a, b) => a.distance - b.distance)
}

/**
 * Get positions near take profit
 */
export function getPositionsNearTakeProfit(
  trades: Trade[], 
  thresholdPercent: number = 3
): Array<Trade & { distance: number }> {
  return trades
    .filter(t => 
      !t.exit_date && 
      t.current_price !== null && 
      t.current_price !== undefined &&
      isNearTakeProfit(t, thresholdPercent)
    )
    .map(t => ({
      ...t,
      distance: calcDistancePercent(t.current_price!, t.take_profit)
    }))
    .sort((a, b) => a.distance - b.distance)
}

/**
 * Get positions with highest risk percentage
 */
export function getHighestRiskPositions(
  trades: Trade[], 
  limit: number = 5
): Array<Trade & { riskPercent: number; riskAmount: number | null }> {
  return trades
    .filter(t => !t.exit_date)
    .map(t => ({
      ...t,
      riskPercent: calcRiskPercent(t.entry_price, t.stop_loss, t.long_short),
      riskAmount: calcPositionRisk(t.entry_price, t.stop_loss, t.quantity, t.contract_size, t.long_short)
    }))
    .sort((a, b) => b.riskPercent - a.riskPercent)
    .slice(0, limit)
}

/**
 * Calculate total risk across open positions (grouped by currency)
 */
export function calcTotalRisk(trades: Trade[]): Record<string, number> {
  const riskByCurrency: Record<string, number> = {}
  
  trades
    .filter(t => !t.exit_date)
    .forEach(t => {
      const risk = calcPositionRisk(t.entry_price, t.stop_loss, t.quantity, t.contract_size, t.long_short)
      if (risk !== null) {
        const currency = t.currency || 'USD'
        riskByCurrency[currency] = (riskByCurrency[currency] || 0) + risk
      }
    })
  
  return riskByCurrency
}

/**
 * Total risk exposure as % of current account equity (initial balance + realized + unrealized PnL).
 * Matches the Total Risk Exposure dashboard widget calculation.
 */
export function calcAccountRiskPercent(
  trades: Trade[],
  accounts: AccountForRiskCalc[],
  selectedCurrency: string,
  latestRates: Map<string, number>,
  options?: {
    accountId?: string
    openTrades?: Trade[]
  },
): number | null {
  if (accounts.length === 0) return null

  const accId = options?.accountId || ''
  const openTrades = options?.openTrades ?? trades.filter((t) => !t.exit_date)

  const totalRiskByCurrency = calcTotalRisk(openTrades)
  let totalRiskInDisplayCurrency = 0
  for (const [currency, amount] of Object.entries(totalRiskByCurrency)) {
    const curr = currency || 'USD'
    totalRiskInDisplayCurrency += convertToDisplayCurrency(amount, curr, selectedCurrency, latestRates)
  }

  const targetAccounts = accId ? accounts.filter((a) => a.id === accId) : accounts
  const accountsWithBalance = targetAccounts.filter(
    (a) => a.initial_balance != null && !isNaN(Number(a.initial_balance)),
  )
  if (accountsWithBalance.length === 0) return null

  const totalInitialInDisplay = accountsWithBalance.reduce(
    (sum, a) =>
      sum + convertAmountWithRates(Number(a.initial_balance!), a.currency, selectedCurrency, latestRates),
    0,
  )

  const relevantAccountIds = new Set(accountsWithBalance.map((a) => a.id))
  const relevantTrades = accId
    ? trades.filter((t) => t.account_id === accId)
    : trades.filter((t) => t.account_id != null && relevantAccountIds.has(t.account_id))

  const realizedPnl = relevantTrades
    .filter((t) => t.exit_date && t.pnl != null)
    .reduce((sum, t) => {
      const pnl = Number(t.pnl)
      return isNaN(pnl)
        ? sum
        : sum + convertAmountWithRates(pnl, t.currency || selectedCurrency, selectedCurrency, latestRates)
    }, 0)

  const unrealizedPnl = relevantTrades
    .filter((t) => !t.exit_date && t.unrealized_pnl != null)
    .reduce((sum, t) => {
      const pnl = Number(t.unrealized_pnl)
      return isNaN(pnl)
        ? sum
        : sum + convertAmountWithRates(pnl, t.currency || selectedCurrency, selectedCurrency, latestRates)
    }, 0)

  const currentEquityInDisplay = totalInitialInDisplay + realizedPnl + unrealizedPnl
  if (currentEquityInDisplay <= 0) return null

  const pct = (totalRiskInDisplayCurrency / currentEquityInDisplay) * 100
  return isNaN(pct) ? null : pct
}

/** Filter open trades using Total Risk Exposure widget filter settings. */
export function filterOpenTradesByWidgetSettings<T extends Trade>(
  openTrades: T[],
  filterSettings?: WidgetFilterSettings | null,
): T[] {
  if (!filterSettings) return openTrades
  return openTrades.filter((trade) => {
    if (filterSettings.market && filterSettings.market !== 'all' && trade.market !== filterSettings.market)
      return false
    if (filterSettings.exchange && filterSettings.exchange !== 'all') {
      const canon = canonicalizeExchange(trade.exchange)
      if ((canon || '').toLowerCase() !== filterSettings.exchange.toLowerCase()) return false
    }
    if (filterSettings.accountId && trade.account_id !== filterSettings.accountId) return false
    if (filterSettings.tag && !(trade.tags ?? []).includes(filterSettings.tag)) return false
    return true
  })
}

// ---------------------------------------------------------------------------
// Widget filter and closed-trades metrics (for dashboard metric widgets)
// ---------------------------------------------------------------------------

export type WidgetDateRange = 'all' | '1d' | '1w' | '1m' | '3m' | '6m' | 'ytd' | '1y'

export type WidgetMarket = 'all' | 'Commodity' | 'Crypto' | 'Forex' | 'Stock'

export interface WidgetFilterSettings {
  dateRange: WidgetDateRange
  strategyId: string
  exchange: string
  market: WidgetMarket
  accountId: string
  tag: string
}

export const DEFAULT_WIDGET_FILTER: WidgetFilterSettings = {
  dateRange: 'all',
  strategyId: '',
  exchange: 'all',
  market: 'all',
  accountId: '',
  tag: '',
}

const DATE_RANGE_ABBREV: Record<WidgetDateRange, string> = {
  all: '',
  '1d': '1D',
  '1w': '1W',
  '1m': '1M',
  '3m': '3M',
  '6m': '6M',
  ytd: 'YTD',
  '1y': '1Y',
}

const EXCHANGE_ABBREV: Record<string, string> = {
  'US Exchanges': 'US',
  BIST: 'BIST',
  NASDAQ: 'NQ',
  NYSE: 'NY',
}

const MARKET_ABBREV: Record<string, string> = {
  Stock: 'Stk',
  Forex: 'FX',
  Crypto: 'Cry',
  Commodity: 'Cmd',
}

/**
 * Format filter criteria as short string for widget subtitle, e.g. "YTD US Darvas"
 */
export function formatFilterCriteria(
  settings: WidgetFilterSettings,
  strategies: { id: string; display_name: string }[],
  accounts?: { id: string; name: string }[]
): string {
  const parts: string[] = []
  const dr = DATE_RANGE_ABBREV[settings.dateRange]
  if (dr) parts.push(dr)
  if (settings.exchange && settings.exchange !== 'all') {
    parts.push(EXCHANGE_ABBREV[settings.exchange] ?? settings.exchange.slice(0, 4))
  }
  if (settings.market && settings.market !== 'all') {
    parts.push(MARKET_ABBREV[settings.market] ?? settings.market.slice(0, 3))
  }
  if (settings.strategyId) {
    const s = strategies.find((x) => x.id === settings.strategyId)
    const name = s?.display_name ?? ''
    parts.push(name.slice(0, 6))
  }
  if (settings.accountId) {
    const acc = accounts?.find((x) => x.id === settings.accountId)
    const name = acc?.name ?? settings.accountId.slice(0, 4)
    parts.push(name.slice(0, 8))
  }
  if (settings.tag) {
    parts.push(settings.tag.slice(0, 8))
  }
  return parts.join(' ')
}

export interface ClosedTradeForMetrics {
  exit_date: string | null
  entry_date: string | null
  pnl: number | null
  pnl_percentage: number | null
  strategy_id: string | null
  exchange?: string | null
  market?: string | null
  currency?: string | null
  entry_price?: number | null
  exit_price?: number | null
  stop_loss?: number | null
  long_short?: string | null
  total_entry_amount?: number | null
  position_size?: number | null
  /** When set, used instead of pnl for currency-based metrics (avgWin, avgLoss, etc.) */
  convertedPnl?: number | null
}

export interface ClosedTradesMetrics {
  totalTrades: number
  totalPnl: number
  winRate: number
  profitFactor: number
  avgPnlPct: number | null
  weightedPnlPct: number | null
  expectancy: number | null
  avgWin: number
  avgLoss: number
  avgRR: number
}

/**
 * Canonicalize exchange name (e.g. group US exchanges)
 */
export function canonicalizeExchange(raw: string | null | undefined): string | null {
  if (!raw) return null
  const trimmed = raw.trim()
  if (!trimmed) return null
  const upper = trimmed.toUpperCase()
  const isNasdaq =
    upper === 'NASDAQ' ||
    upper.includes('NASDAQ STOCK') ||
    upper.includes('NASDAQ GLOBAL') ||
    upper.includes('NASDAQ CAPITAL') ||
    upper.includes('NASDAQ GS') ||
    upper.includes('NASDAQ GM')
  const isNyse =
    upper === 'NYSE' ||
    upper.includes('NEW YORK STOCK EXCHANGE') ||
    upper.includes('NYSE AMERICAN') ||
    upper.includes('NYSE ARCA')
  if (isNasdaq || isNyse) return 'US Exchanges'
  return trimmed
}

function getDateRangeBounds(range: WidgetDateRange): { start: string; end: string } | null {
  const today = new Date()
  const format = (d: Date) => d.toISOString().split('T')[0]
  const end = format(today)
  let start = ''
  switch (range) {
    case '1d': {
      const d = new Date(today)
      d.setDate(d.getDate() - 1)
      start = format(d)
      break
    }
    case '1w': {
      const d = new Date(today)
      d.setDate(d.getDate() - 7)
      start = format(d)
      break
    }
    case '1m': {
      const d = new Date(today)
      d.setMonth(d.getMonth() - 1)
      start = format(d)
      break
    }
    case '3m': {
      const d = new Date(today)
      d.setMonth(d.getMonth() - 3)
      start = format(d)
      break
    }
    case '6m': {
      const d = new Date(today)
      d.setMonth(d.getMonth() - 6)
      start = format(d)
      break
    }
    case 'ytd': {
      start = format(new Date(today.getFullYear(), 0, 1))
      break
    }
    case '1y': {
      const d = new Date(today)
      d.setFullYear(d.getFullYear() - 1)
      start = format(d)
      break
    }
    case 'all':
      return null
    default:
      return null
  }
  return { start, end }
}

function isWithinDateRange(dateStr: string | null, range: WidgetDateRange): boolean {
  if (!dateStr) return false
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return false
  const bounds = getDateRangeBounds(range)
  if (!bounds) return true
  const start = new Date(bounds.start)
  if (date < start) return false
  const end = new Date(bounds.end)
  end.setHours(23, 59, 59, 999)
  if (date > end) return false
  return true
}

/**
 * Filter trades by widget settings (date, strategy, exchange, market)
 */
export function applyWidgetFilters<T extends ClosedTradeForMetrics>(
  trades: T[],
  settings: WidgetFilterSettings
): T[] {
  return trades.filter((t) => {
    const dateForFilter = t.exit_date || t.entry_date
    if (!isWithinDateRange(dateForFilter, settings.dateRange)) return false
    if (settings.strategyId && t.strategy_id !== settings.strategyId) return false
    if (settings.exchange && settings.exchange !== 'all') {
      const canon = canonicalizeExchange(t.exchange)
      if ((canon || '').toLowerCase() !== settings.exchange.toLowerCase()) return false
    }
    if (settings.market && settings.market !== 'all' && t.market !== settings.market) return false
    return true
  })
}

/**
 * Compute realized R (reward/risk) from entry, exit, stop loss
 */
export function computeRealizedR(t: ClosedTradeForMetrics): number | null {
  const entry = t.entry_price
  const exit = t.exit_price
  const sl = t.stop_loss
  if (entry == null || exit == null || sl == null) return null
  const ls = ((t.long_short || '').toLowerCase() === 'short') ? 'short' : 'long'
  const risk = ls === 'long' ? entry - sl : sl - entry
  if (risk <= 0) return null
  const reward = ls === 'long' ? exit - entry : entry - exit
  return reward / risk
}

/**
 * Get P&L for metrics (uses convertedPnl when available for currency display)
 */
function getPnlForMetrics(t: ClosedTradeForMetrics): number {
  const pnl = t.convertedPnl ?? t.pnl ?? 0
  return pnl
}

/**
 * Calculate closed-trades metrics (for dashboard metric widgets)
 * Uses convertedPnl when set (for currency conversion), otherwise raw pnl
 */
export function calcClosedTradesMetrics(
  trades: ClosedTradeForMetrics[]
): ClosedTradesMetrics {
  const closed = trades.filter((t) => t.exit_date && (t.pnl !== null || t.convertedPnl !== null))
  const totalTrades = closed.length
  const totalPnl = closed.reduce((sum, t) => sum + getPnlForMetrics(t), 0)
  const winningTrades = closed.filter((t) => getPnlForMetrics(t) > 0)
  const losingTrades = closed.filter((t) => getPnlForMetrics(t) < 0)
  const totalProfit = winningTrades.reduce((sum, t) => sum + getPnlForMetrics(t), 0)
  const totalLoss = Math.abs(losingTrades.reduce((sum, t) => sum + getPnlForMetrics(t), 0))
  const winRate = totalTrades > 0 ? (winningTrades.length / totalTrades) * 100 : 0
  const profitFactor =
    totalLoss > 0 ? totalProfit / totalLoss : totalProfit > 0 ? Infinity : 0
  const avgWin = winningTrades.length > 0 ? totalProfit / winningTrades.length : 0
  const avgLoss = losingTrades.length > 0 ? totalLoss / losingTrades.length : 0
  const avgRR = avgLoss > 0 ? avgWin / avgLoss : 0

  const pnlPctTrades = closed.filter((t) => t.pnl_percentage !== null)
  const avgPnlPct =
    pnlPctTrades.length > 0
      ? pnlPctTrades.reduce((sum, t) => sum + (t.pnl_percentage ?? 0), 0) / pnlPctTrades.length
      : null

  const weightSize = (t: ClosedTradeForMetrics) =>
    t.total_entry_amount ?? t.position_size ?? 0
  const weightedPnlTrades = closed.filter(
    (t) => t.pnl_percentage !== null && weightSize(t) > 0
  )
  const totalPositionSize = weightedPnlTrades.reduce((sum, t) => sum + weightSize(t), 0)
  const weightedPnlPct =
    totalPositionSize > 0
      ? weightedPnlTrades.reduce(
          (sum, t) => sum + weightSize(t) * (t.pnl_percentage ?? 0),
          0
        ) / totalPositionSize
      : null

  const winningWithR = winningTrades.filter((t) => computeRealizedR(t) !== null)
  const losingWithR = losingTrades.filter((t) => computeRealizedR(t) !== null)
  const tradesWithRCount = winningWithR.length + losingWithR.length
  const avgWinR =
    winningWithR.length > 0
      ? winningWithR.reduce((s, t) => s + (computeRealizedR(t) ?? 0), 0) / winningWithR.length
      : 0
  const avgLossR =
    losingWithR.length > 0
      ? losingWithR.reduce(
          (s, t) => s + Math.abs(computeRealizedR(t) ?? 0),
          0
        ) / losingWithR.length
      : 0
  const winRateForExp = tradesWithRCount > 0 ? winningWithR.length / tradesWithRCount : 0
  const lossRateForExp = tradesWithRCount > 0 ? losingWithR.length / tradesWithRCount : 0
  const expectancy =
    tradesWithRCount > 0 ? winRateForExp * avgWinR - lossRateForExp * avgLossR : null

  return {
    totalTrades,
    totalPnl,
    winRate,
    profitFactor,
    avgPnlPct,
    weightedPnlPct,
    expectancy,
    avgWin,
    avgLoss,
    avgRR,
  }
}

/** One point on the cumulative win-rate curve (after each closed trade, chronological by exit). */
export interface CumulativeWinRatePoint {
  /** Exit time (ms) for horizontal scale */
  exitTime: number
  /** Cumulative win % after this trade (wins / count × 100), same definition as metrics win rate */
  cumulativeWinRatePct: number
}

/**
 * Series of cumulative win rate over time for closed trades (widget sparkline).
 * Uses convertedPnl when set; win if P&L > 0. Sorted by exit_date ascending.
 */
export function computeCumulativeWinRateSeries(
  trades: ClosedTradeForMetrics[]
): CumulativeWinRatePoint[] {
  const closed = trades.filter(
    (t) => t.exit_date && (t.pnl !== null || t.convertedPnl !== null)
  )
  if (closed.length === 0) return []
  const sorted = [...closed].sort((a, b) => {
    const ta = new Date(a.exit_date!).getTime()
    const tb = new Date(b.exit_date!).getTime()
    if (ta !== tb) return ta - tb
    return (a.entry_date || '').localeCompare(b.entry_date || '')
  })
  let wins = 0
  const out: CumulativeWinRatePoint[] = []
  for (let i = 0; i < sorted.length; i++) {
    const pnl = sorted[i].convertedPnl ?? sorted[i].pnl ?? 0
    if (pnl > 0) wins++
    const n = i + 1
    out.push({
      exitTime: new Date(sorted[i].exit_date!).getTime(),
      cumulativeWinRatePct: (wins / n) * 100,
    })
  }
  return out
}

/** One point on cumulative profit-factor curve (aligned with headline profit factor formula). */
export interface CumulativeProfitFactorPoint {
  exitTime: number
  cumulativeProfitFactor: number
}

/**
 * Series of cumulative profit factor over time for closed trades (widget sparkline).
 * Uses convertedPnl when set; same rules as headline profit factor after each chronological close.
 */
export function computeCumulativeProfitFactorSeries(
  trades: ClosedTradeForMetrics[]
): CumulativeProfitFactorPoint[] {
  const closed = trades.filter(
    (t) => t.exit_date && (t.pnl !== null || t.convertedPnl !== null)
  )
  if (closed.length === 0) return []
  const sorted = [...closed].sort((a, b) => {
    const ta = new Date(a.exit_date!).getTime()
    const tb = new Date(b.exit_date!).getTime()
    if (ta !== tb) return ta - tb
    return (a.entry_date || '').localeCompare(b.entry_date || '')
  })
  let profitSum = 0
  let lossAbsSum = 0
  const out: CumulativeProfitFactorPoint[] = []
  for (let i = 0; i < sorted.length; i++) {
    const pnl = sorted[i].convertedPnl ?? sorted[i].pnl ?? 0
    if (pnl > 0) profitSum += pnl
    else if (pnl < 0) lossAbsSum += Math.abs(pnl)

    let pf: number
    if (lossAbsSum > 0) pf = profitSum / lossAbsSum
    else if (profitSum > 0) pf = Number.POSITIVE_INFINITY
    else pf = 0

    out.push({
      exitTime: new Date(sorted[i].exit_date!).getTime(),
      cumulativeProfitFactor: pf,
    })
  }
  return out
}

/**
 * Get unique exchanges from trades (canonicalized, for filter dropdown)
 */
export function getUniqueExchangesFromTrades<T extends { exchange?: string | null }>(
  trades: T[]
): string[] {
  const set = new Set<string>()
  for (const t of trades) {
    const canon = canonicalizeExchange(t.exchange)
    if (canon && canon.trim()) set.add(canon)
  }
  return Array.from(set).sort((a, b) => {
    if (a === 'US Exchanges' && b !== 'US Exchanges') return -1
    if (b === 'US Exchanges' && a !== 'US Exchanges') return 1
    return a.localeCompare(b)
  })
}

/** Exchange display name for insights (user-friendly labels) */
export function getExchangeDisplayName(canonical: string): string {
  if (canonical === 'US Exchanges') return 'US Stock Market'
  return canonical
}

export interface PerformanceGroupMetrics {
  key: string
  displayName: string
  totalTrades: number
  totalPnl: number
  avgPnlPct: number | null
  winRate: number
  profitFactor: number
}

export interface PerformanceInsightsResult {
  bestStrategy: PerformanceGroupMetrics | null
  worstStrategy: PerformanceGroupMetrics | null
  bestMarket: PerformanceGroupMetrics | null
  worstMarket: PerformanceGroupMetrics | null
  bestTimeframe: PerformanceGroupMetrics | null
  worstTimeframe: PerformanceGroupMetrics | null
  bestExchange: PerformanceGroupMetrics | null
  worstExchange: PerformanceGroupMetrics | null
  dataQuality: {
    strategyCoveragePct: number
    timeframeCoveragePct: number
    exchangeCoveragePct: number
    totalClosedTrades: number
  }
}

const MIN_TRADES_FOR_INSIGHT = 2
const DATA_QUALITY_THRESHOLD_PCT = 70

function calcGroupMetrics(
  trades: ClosedTradeForMetrics[],
  getPnl: (t: ClosedTradeForMetrics) => number
): { winRate: number; profitFactor: number; totalPnl: number; avgPnlPct: number | null } {
  const closed = trades.filter((t) => t.exit_date && (t.pnl !== null || t.convertedPnl !== null))
  const totalTrades = closed.length
  const totalPnl = closed.reduce((sum, t) => sum + getPnl(t), 0)
  const winningTrades = closed.filter((t) => getPnl(t) > 0)
  const losingTrades = closed.filter((t) => getPnl(t) < 0)
  const totalProfit = winningTrades.reduce((sum, t) => sum + getPnl(t), 0)
  const totalLoss = Math.abs(losingTrades.reduce((sum, t) => sum + getPnl(t), 0))
  const winRate = totalTrades > 0 ? (winningTrades.length / totalTrades) * 100 : 0
  const profitFactor =
    totalLoss > 0 ? totalProfit / totalLoss : totalProfit > 0 ? Infinity : 0
  const pnlPctTrades = closed.filter((t) => t.pnl_percentage != null)
  const avgPnlPct =
    pnlPctTrades.length > 0
      ? pnlPctTrades.reduce((sum, t) => sum + (t.pnl_percentage ?? 0), 0) / pnlPctTrades.length
      : null
  return { winRate, profitFactor, totalPnl, avgPnlPct }
}

function getGroupKey(
  t: ClosedTradeForMetrics,
  dim: 'strategy' | 'market' | 'timeframe' | 'exchange'
): string {
  if (dim === 'strategy') return t.strategy_id || '__none__'
  if (dim === 'market') return t.market || '__none__'
  if (dim === 'timeframe') return (t as { time_interval?: string | null }).time_interval || '__none__'
  if (dim === 'exchange') return canonicalizeExchange(t.exchange) || '__none__'
  return '__none__'
}

/**
 * Compute performance insights: best/worst strategy, market, timeframe, exchange.
 * Uses closed trades only. Requires at least MIN_TRADES_FOR_INSIGHT per group.
 */
export function calcPerformanceInsights(
  trades: ClosedTradeForMetrics[],
  dateRange: WidgetDateRange,
  strategyNames: Record<string, string>
): PerformanceInsightsResult {
  const filtered = applyWidgetFilters(trades, {
    dateRange,
    strategyId: '',
    exchange: 'all',
    market: 'all',
  })
  const closed = filtered.filter(
    (t) => t.exit_date && (t.pnl !== null || t.convertedPnl !== null)
  )
  const getPnl = (t: ClosedTradeForMetrics) => t.convertedPnl ?? t.pnl ?? 0

  const dataQuality = {
    totalClosedTrades: closed.length,
    strategyCoveragePct:
      closed.length > 0
        ? (closed.filter((t) => t.strategy_id && t.strategy_id.trim()).length / closed.length) * 100
        : 0,
    timeframeCoveragePct:
      closed.length > 0
        ? (closed.filter((t) => {
            const ti = (t as { time_interval?: string | null }).time_interval
            return ti && ti.trim()
          }).length / closed.length) * 100
        : 0,
    exchangeCoveragePct:
      closed.length > 0
        ? (closed.filter((t) => canonicalizeExchange(t.exchange)).length / closed.length) * 100
        : 0,
  }

  const groupBy = (
    dim: 'strategy' | 'market' | 'timeframe' | 'exchange'
  ): Map<string, PerformanceGroupMetrics> => {
    const map = new Map<string, ClosedTradeForMetrics[]>()
    for (const t of closed) {
      const key = getGroupKey(t, dim)
      if (key === '__none__') continue
      const list = map.get(key) || []
      list.push(t)
      map.set(key, list)
    }
    const result = new Map<string, PerformanceGroupMetrics>()
    for (const [key, list] of map) {
      if (list.length < MIN_TRADES_FOR_INSIGHT) continue
      const metrics = calcGroupMetrics(list, getPnl)
      let displayName = key
      if (dim === 'strategy') displayName = strategyNames[key] || key
      if (dim === 'exchange') displayName = getExchangeDisplayName(key)
      result.set(key, {
        key,
        displayName,
        totalTrades: list.length,
        ...metrics,
      })
    }
    return result
  }

  const pickBest = (map: Map<string, PerformanceGroupMetrics>): PerformanceGroupMetrics | null => {
    let best: PerformanceGroupMetrics | null = null
    for (const m of map.values()) {
      if (!best || m.totalPnl > best.totalPnl) best = m
    }
    return best
  }

  const pickWorst = (map: Map<string, PerformanceGroupMetrics>): PerformanceGroupMetrics | null => {
    let worst: PerformanceGroupMetrics | null = null
    for (const m of map.values()) {
      if (!worst || m.totalPnl < worst.totalPnl) worst = m
    }
    return worst
  }

  const byStrategy = groupBy('strategy')
  const byMarket = groupBy('market')
  const byTimeframe = groupBy('timeframe')
  const byExchange = groupBy('exchange')

  return {
    bestStrategy: pickBest(byStrategy),
    worstStrategy: pickWorst(byStrategy),
    bestMarket: pickBest(byMarket),
    worstMarket: pickWorst(byMarket),
    bestTimeframe: pickBest(byTimeframe),
    worstTimeframe: pickWorst(byTimeframe),
    bestExchange: pickBest(byExchange),
    worstExchange: pickWorst(byExchange),
    dataQuality,
  }
}

export interface RiskManagementInsightsResult {
  averageRiskPercent: number | null
  stopLossUsageRate: number
  averageRR: number | null
  totalTrades: number
}

/**
 * Compute risk management insights: average risk %, stop loss usage rate, average R:R.
 */
export function calcRiskManagementInsights(
  trades: ClosedTradeForMetrics[],
  dateRange: WidgetDateRange
): RiskManagementInsightsResult {
  const filtered = applyWidgetFilters(trades, {
    dateRange,
    strategyId: '',
    exchange: 'all',
    market: 'all',
  })
  const closed = filtered.filter(
    (t) => t.exit_date && (t.pnl !== null || t.convertedPnl !== null)
  )

  const tradesWithRisk = closed.filter(
    (t) =>
      t.entry_price != null &&
      t.stop_loss != null &&
      Math.abs((t.entry_price ?? 0) - (t.stop_loss ?? 0)) > 0
  )
  const averageRiskPercent =
    tradesWithRisk.length > 0
      ? tradesWithRisk.reduce(
          (sum, t) => sum + calcRiskPercent(t.entry_price!, t.stop_loss!),
          0
        ) / tradesWithRisk.length
      : null

  const tradesWithValidSL = closed.filter(
    (t) =>
      t.entry_price != null &&
      t.stop_loss != null &&
      Math.abs((t.entry_price ?? 0) - (t.stop_loss ?? 0)) > 0
  )
  const stopLossUsageRate =
    closed.length > 0 ? (tradesWithValidSL.length / closed.length) * 100 : 0

  const tradesWithR = closed.filter((t) => computeRealizedR(t) !== null)
  const averageRR =
    tradesWithR.length > 0
      ? tradesWithR.reduce((sum, t) => sum + (computeRealizedR(t) ?? 0), 0) /
        tradesWithR.length
      : null

  return {
    averageRiskPercent,
    stopLossUsageRate,
    averageRR,
    totalTrades: closed.length,
  }
}

export interface ExecutionInsightsResult {
  averagePlannedRR: number | null
  averageRealizedRR: number | null
  totalTradesWithBoth: number
}

type TradeWithPlannedRR = ClosedTradeForMetrics & { rr?: number | null }

/**
 * Compute execution insights: planned R:R vs realized R:R comparison.
 */
export function calcExecutionInsights(
  trades: TradeWithPlannedRR[],
  dateRange: WidgetDateRange
): ExecutionInsightsResult {
  const filtered = applyWidgetFilters(trades, {
    dateRange,
    strategyId: '',
    exchange: 'all',
    market: 'all',
  })
  const closed = filtered.filter(
    (t) => t.exit_date && (t.pnl !== null || t.convertedPnl !== null)
  )

  const tradesWithBoth = closed.filter((t) => {
    const realized = computeRealizedR(t)
    const planned = t.rr
    return realized !== null && planned != null && planned > 0
  })

  const averagePlannedRR =
    tradesWithBoth.length > 0
      ? tradesWithBoth.reduce((sum, t) => sum + (t.rr ?? 0), 0) / tradesWithBoth.length
      : null

  const averageRealizedRR =
    tradesWithBoth.length > 0
      ? tradesWithBoth.reduce(
          (sum, t) => sum + (computeRealizedR(t) ?? 0),
          0
        ) / tradesWithBoth.length
      : null

  return {
    averagePlannedRR,
    averageRealizedRR,
    totalTradesWithBoth: tradesWithBoth.length,
  }
}

export interface MistakeDetectionResult {
  totalTrades: number
  tradesWithoutStopLoss: number
  noStopLossPct: number
  tradesWithPlannedRR: number
  tradesBelowPlannedRR: number
  belowPlannedRRPct: number | null
}

/**
 * Compute mistake detection: trades without stop loss, exits below planned R:R.
 */
export function calcMistakeDetectionInsights(
  trades: (ClosedTradeForMetrics & { rr?: number | null })[],
  dateRange: WidgetDateRange
): MistakeDetectionResult {
  const filtered = applyWidgetFilters(trades, {
    dateRange,
    strategyId: '',
    exchange: 'all',
    market: 'all',
  })
  const closed = filtered.filter(
    (t) => t.exit_date && (t.pnl !== null || t.convertedPnl !== null)
  )

  const tradesWithoutStopLoss = closed.filter(
    (t) =>
      t.entry_price == null ||
      t.stop_loss == null ||
      Math.abs((t.entry_price ?? 0) - (t.stop_loss ?? 0)) <= 0
  )
  const noStopLossPct =
    closed.length > 0 ? (tradesWithoutStopLoss.length / closed.length) * 100 : 0

  const tradesWithPlannedRR = closed.filter(
    (t) => computeRealizedR(t) !== null && t.rr != null && t.rr > 0
  )
  const tradesBelowPlannedRR = tradesWithPlannedRR.filter(
    (t) => (computeRealizedR(t) ?? 0) < (t.rr ?? 0)
  )
  const belowPlannedRRPct =
    tradesWithPlannedRR.length > 0
      ? (tradesBelowPlannedRR.length / tradesWithPlannedRR.length) * 100
      : null

  return {
    totalTrades: closed.length,
    tradesWithoutStopLoss: tradesWithoutStopLoss.length,
    noStopLossPct,
    tradesWithPlannedRR: tradesWithPlannedRR.length,
    tradesBelowPlannedRR: tradesBelowPlannedRR.length,
    belowPlannedRRPct,
  }
}

export interface PositionSizeDisciplineResult {
  averagePositionSize: number | null
  coefficientOfVariation: number | null
  totalTrades: number
  hasHighVariance: boolean
}

const POSITION_SIZE_CV_THRESHOLD = 0.6

/**
 * Compute position size discipline: average and coefficient of variation.
 * Input: array of position sizes in a single currency (already converted).
 * Coefficient of variation (CV) = stdDev / mean. High CV = inconsistent position sizing.
 */
export function calcPositionSizeDisciplineInsights(
  convertedPositionSizes: number[]
): PositionSizeDisciplineResult {
  const valid = convertedPositionSizes.filter((s) => s > 0)
  if (valid.length < 2) {
    return {
      averagePositionSize: valid.length === 1 ? valid[0] : null,
      coefficientOfVariation: null,
      totalTrades: valid.length,
      hasHighVariance: false,
    }
  }

  const mean = valid.reduce((a, b) => a + b, 0) / valid.length
  const variance =
    valid.reduce((sum, x) => sum + (x - mean) ** 2, 0) / valid.length
  const stdDev = Math.sqrt(variance)
  const coefficientOfVariation = mean > 0 ? stdDev / mean : null

  return {
    averagePositionSize: mean,
    coefficientOfVariation,
    totalTrades: valid.length,
    hasHighVariance:
      coefficientOfVariation != null &&
      coefficientOfVariation >= POSITION_SIZE_CV_THRESHOLD,
  }
}
